import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, Users, Clock, ThumbsUp,
  Mail, MessageCircle, Facebook, MessageSquare,
  ArrowUpRight, ArrowDownRight, Loader2, Calendar,
  AlertTriangle, CheckCircle, BarChart3, Star, User
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getAnalytics, getAgentActivity, getSatisfactionTrends, getAgentProductivity, getUsers } from '../../services/api';

const channelIcons = {
  Email: Mail,
  WhatsApp: MessageCircle,
  Facebook: Facebook,
  'Live Chat': MessageSquare
};

const channelColors = {
  Email: 'bg-blue-500',
  WhatsApp: 'bg-green-500',
  Facebook: 'bg-indigo-500',
  'Live Chat': 'bg-orange-500'
};

const AnalyticsView = () => {
  const [statistics, setStatistics] = useState(null);
  const [agentActivity, setAgentActivity] = useState(null);
  const [satisfactionData, setSatisfactionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [satisfactionLoading, setSatisfactionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [satisfactionPeriod, setSatisfactionPeriod] = useState('30d');
  
  // Agent filter for productivity
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agentProductivity, setAgentProductivity] = useState(null);
  const [productivityLoading, setProductivityLoading] = useState(false);
  
  // Date range for analytics (shared across tabs)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Load agents list
  useEffect(() => {
    getUsers().then(users => {
      const agentUsers = users.filter(u => u.role === 'agent' || u.role === 'admin');
      setAgents(agentUsers);
    }).catch(console.error);
  }, []);

  // Load agent productivity when agent is selected
  const loadAgentProductivity = useCallback(async () => {
    if (selectedAgent === 'all') {
      setAgentProductivity(null);
      return;
    }
    
    setProductivityLoading(true);
    try {
      const data = await getAgentProductivity(selectedAgent, startDate, endDate);
      setAgentProductivity(data);
    } catch (error) {
      console.error('Failed to load agent productivity:', error);
      setAgentProductivity(null);
    } finally {
      setProductivityLoading(false);
    }
  }, [selectedAgent, startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'agents' && selectedAgent !== 'all') {
      loadAgentProductivity();
    }
  }, [activeTab, selectedAgent, loadAgentProductivity]);

  // Load overview analytics with date filter
  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAnalytics(startDate, endDate);
      setStatistics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'overview') {
      loadAnalytics();
    }
  }, [activeTab, loadAnalytics]);

  const loadAgentActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const data = await getAgentActivity(startDate, endDate);
      setAgentActivity(data);
    } catch (error) {
      console.error('Failed to load agent activity:', error);
    } finally {
      setActivityLoading(false);
    }
  }, [startDate, endDate]);

  const loadSatisfactionTrends = useCallback(async () => {
    setSatisfactionLoading(true);
    try {
      const data = await getSatisfactionTrends(satisfactionPeriod);
      setSatisfactionData(data);
    } catch (error) {
      console.error('Failed to load satisfaction trends:', error);
    } finally {
      setSatisfactionLoading(false);
    }
  }, [satisfactionPeriod]);

  useEffect(() => {
    if (activeTab === 'agents') {
      loadAgentActivity();
    } else if (activeTab === 'satisfaction') {
      loadSatisfactionTrends();
    }
  }, [activeTab, loadAgentActivity, loadSatisfactionTrends]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#393d8a]" />
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-gray-500">Failed to load analytics</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Tickets',
      value: statistics.total_tickets?.toLocaleString() || '0',
      change: '+12%',
      trend: 'up',
      icon: Users,
      color: 'bg-[#393d8a]'
    },
    {
      title: 'Open Tickets',
      value: statistics.open_tickets || 0,
      change: '-5%',
      trend: 'down',
      icon: Mail,
      color: 'bg-[#eeb224]'
    },
    {
      title: 'Escalated',
      value: statistics.escalated_tickets || 0,
      change: statistics.escalated_tickets > 0 ? 'Needs attention' : 'All good',
      trend: statistics.escalated_tickets > 0 ? 'up' : 'down',
      icon: AlertTriangle,
      color: statistics.escalated_tickets > 0 ? 'bg-red-500' : 'bg-gray-400'
    },
    {
      title: 'Resolved Today',
      value: statistics.resolved_today || 0,
      change: '+8%',
      trend: 'up',
      icon: CheckCircle,
      color: 'bg-[#30a34b]'
    }
  ];

  const maxTickets = Math.max(...(statistics.weekly_trend || []).map(d => d.tickets), 1);

  return (
    <div className="flex-1 bg-gray-50 p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-gray-500 mt-1">Monitor your support team's performance</p>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex gap-2 bg-white rounded-lg p-1 border border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all",
                activeTab === 'overview' 
                  ? "bg-[#393d8a] text-white" 
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('agents')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all",
                activeTab === 'agents' 
                  ? "bg-[#393d8a] text-white" 
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Agent Activity
            </button>
            <button
              onClick={() => setActiveTab('satisfaction')}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all",
                activeTab === 'satisfaction' 
                  ? "bg-[#393d8a] text-white" 
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              Satisfaction Trends
            </button>
          </div>
        </div>

        {activeTab === 'overview' ? (
          <>
            {/* Date Filter for Overview */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <Label className="text-sm text-gray-600 mb-1 block">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm text-gray-600 mb-1 block">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <Button 
                  onClick={loadAnalytics}
                  className="bg-[#393d8a] hover:bg-[#393d8a]/90 gap-2"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  Apply
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date().toISOString().split('T')[0];
                      setStartDate(today);
                      setEndDate(today);
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(start.getDate() - 7);
                      setStartDate(start.toISOString().split('T')[0]);
                      setEndDate(end.toISOString().split('T')[0]);
                    }}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const end = new Date();
                      const start = new Date();
                      start.setDate(start.getDate() - 30);
                      setStartDate(start.toISOString().split('T')[0]);
                      setEndDate(end.toISOString().split('T')[0]);
                    }}
                  >
                    Last 30 Days
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Showing data from {startDate} to {endDate}
              </p>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {statCards.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        stat.color
                      )}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className={cn(
                        "flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full",
                        stat.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      )}>
                        {stat.trend === 'up' ? (
                          <ArrowUpRight className="w-3 h-3" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3" />
                        )}
                        {stat.change}
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.title}</p>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-6">
              {/* Weekly Trend Chart */}
              <div className="col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-6">Weekly Ticket Trend</h3>
                <div className="flex items-end justify-between h-48 gap-4">
                  {(statistics.weekly_trend || []).map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div 
                        className="w-full bg-gradient-to-t from-[#393d8a] to-[#30a34b] rounded-t-lg transition-all duration-500 hover:opacity-80"
                        style={{ height: `${(day.tickets / maxTickets) * 100}%`, minHeight: '8px' }}
                      />
                      <span className="text-xs text-gray-500 font-medium">{day.day}</span>
                      <span className="text-xs font-semibold text-gray-700">{day.tickets}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Channel Distribution */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-6">Tickets by Channel</h3>
                <div className="space-y-4">
                  {(statistics.tickets_by_channel || []).map((channel, index) => {
                    const Icon = channelIcons[channel.channel] || Mail;
                    return (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center",
                              channelColors[channel.channel]
                            )}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">{channel.channel}</span>
                          </div>
                          <span className="text-sm text-gray-500">{channel.count}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={cn("h-full rounded-full transition-all duration-500", channelColors[channel.channel])}
                            style={{ width: `${channel.percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Customer Satisfaction */}
            <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Customer Satisfaction Score</h3>
                  <p className="text-gray-500 text-sm">Based on post-interaction surveys</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-[#393d8a]">{statistics.customer_satisfaction || 0}%</p>
                    <p className="text-sm text-gray-500 mt-1">CSAT Score</p>
                  </div>
                  <div className="w-32 h-32 relative">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="12"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        fill="none"
                        stroke="url(#gradient)"
                        strokeWidth="12"
                        strokeLinecap="round"
                        strokeDasharray={`${(statistics.customer_satisfaction || 0) * 3.52} 352`}
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#393d8a" />
                          <stop offset="100%" stopColor="#30a34b" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ThumbsUp className="w-8 h-8 text-[#393d8a]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : activeTab === 'agents' ? (
          /* Agent Activity Tab */
          <div className="space-y-6">
            {/* Date Range and Agent Filter */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-end gap-4 flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-sm text-gray-600">Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 min-w-[150px]">
                  <Label className="text-sm text-gray-600">End Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-sm text-gray-600">Filter by Agent</Label>
                  <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents (Overview)</SelectItem>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name || agent.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={() => {
                    if (selectedAgent === 'all') {
                      loadAgentActivity();
                    } else {
                      loadAgentProductivity();
                    }
                  }}
                  className="bg-[#393d8a] hover:bg-[#393d8a]/90"
                  disabled={activityLoading || productivityLoading}
                >
                  {(activityLoading || productivityLoading) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Calendar className="w-4 h-4 mr-2" />
                      Apply
                    </>
                  )}
                </Button>
                
                {/* Quick Date Buttons */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      setStartDate(today.toISOString().split('T')[0]);
                      setEndDate(today.toISOString().split('T')[0]);
                    }}
                  >
                    Today
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const weekAgo = new Date(today);
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      setStartDate(weekAgo.toISOString().split('T')[0]);
                      setEndDate(today.toISOString().split('T')[0]);
                    }}
                  >
                    Last 7 Days
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      const monthAgo = new Date(today);
                      monthAgo.setDate(monthAgo.getDate() - 30);
                      setStartDate(monthAgo.toISOString().split('T')[0]);
                      setEndDate(today.toISOString().split('T')[0]);
                    }}
                  >
                    Last 30 Days
                  </Button>
                </div>
              </div>
            </div>

            {/* Show Individual Agent View or All Agents View */}
            {selectedAgent !== 'all' ? (
              /* Individual Agent Productivity View */
              productivityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#393d8a]" />
                </div>
              ) : agentProductivity ? (
                <>
                  {/* Agent Header */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-[#393d8a] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                        {agentProductivity.agent?.name?.[0] || '?'}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{agentProductivity.agent?.name}</h3>
                        <p className="text-gray-500">{agentProductivity.agent?.email}</p>
                        <p className="text-sm text-gray-400 capitalize">{agentProductivity.agent?.role}</p>
                      </div>
                    </div>
                  </div>

                  {/* Agent Summary Cards */}
                  <div className="grid grid-cols-5 gap-4">
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#393d8a]">{agentProductivity.summary?.total_assigned || 0}</p>
                        <p className="text-sm text-gray-500 mt-1">Assigned</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#30a34b]">{agentProductivity.summary?.total_resolved || 0}</p>
                        <p className="text-sm text-gray-500 mt-1">Resolved</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-[#eeb224]">{agentProductivity.summary?.total_open || 0}</p>
                        <p className="text-sm text-gray-500 mt-1">Open</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-gray-700">{agentProductivity.summary?.total_messages || 0}</p>
                        <p className="text-sm text-gray-500 mt-1">Messages Sent</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-indigo-600">{agentProductivity.summary?.resolution_rate || 0}%</p>
                        <p className="text-sm text-gray-500 mt-1">Resolution Rate</p>
                      </div>
                    </div>
                  </div>

                  {/* Daily Breakdown Table */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900">Daily Productivity</h3>
                      <p className="text-sm text-gray-500">
                        {agentProductivity.date_range?.start} to {agentProductivity.date_range?.end}
                      </p>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Assigned</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Resolved</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Open</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Channels</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {agentProductivity.daily_breakdown?.map((day, idx) => (
                          <tr key={idx} className={day.assigned > 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-gray-900">{day.day}</p>
                                <p className="text-xs text-gray-500">{day.date}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={cn(
                                "inline-flex items-center justify-center w-8 h-8 rounded-full font-medium",
                                day.assigned > 0 ? "bg-[#393d8a]/10 text-[#393d8a]" : "text-gray-400"
                              )}>
                                {day.assigned}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={cn(
                                "inline-flex items-center justify-center w-8 h-8 rounded-full font-medium",
                                day.resolved > 0 ? "bg-green-100 text-green-700" : "text-gray-400"
                              )}>
                                {day.resolved}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={cn(
                                "inline-flex items-center justify-center w-8 h-8 rounded-full font-medium",
                                day.open > 0 ? "bg-yellow-100 text-yellow-700" : "text-gray-400"
                              )}>
                                {day.open}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1">
                                {Object.entries(day.channels || {}).map(([ch, count]) => (
                                  <span key={ch} className={cn(
                                    "text-xs px-2 py-1 rounded-full text-white",
                                    ch === 'email' ? 'bg-blue-500' : ch === 'whatsapp' ? 'bg-green-500' : 'bg-indigo-500'
                                  )}>
                                    {ch}: {count}
                                  </span>
                                ))}
                                {Object.keys(day.channels || {}).length === 0 && (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Select an agent and click Apply to view their productivity</p>
                </div>
              )
            ) : (
              /* All Agents Overview */
              activityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#393d8a]" />
                </div>
              ) : agentActivity ? (
                <>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#393d8a] rounded-lg flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">Total Interactions</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{agentActivity.summary?.total_interactions || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#30a34b] rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">Resolved</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{agentActivity.summary?.total_resolved || 0}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#eeb224] rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">Open</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{agentActivity.summary?.total_open || 0}</p>
                  </div>
                </div>

                {/* Agent Activity Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 border-b border-gray-100">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#393d8a]" />
                      Agent Performance
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {agentActivity.date_range?.start} to {agentActivity.date_range?.end}
                    </p>
                  </div>
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Messages Sent</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Assigned</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Resolved</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Open</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(agentActivity.agent_stats || []).map((agent, index) => (
                        <tr key={agent.agent_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className={cn(
                                "w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold",
                                index === 0 ? "bg-[#393d8a]" : "bg-gray-400"
                              )}>
                                {agent.avatar}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{agent.agent_name}</p>
                                <p className="text-sm text-gray-500">{agent.agent_email}</p>
                              </div>
                              {agent.role === 'admin' && (
                                <span className="px-2 py-0.5 bg-[#eeb224]/10 text-[#eeb224] text-xs font-medium rounded-full">
                                  Admin
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={cn(
                              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                              agent.status === 'online' ? 'bg-green-100 text-green-700' :
                              agent.status === 'away' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            )}>
                              <span className={cn(
                                "w-1.5 h-1.5 rounded-full mr-1.5",
                                agent.status === 'online' ? 'bg-green-500' :
                                agent.status === 'away' ? 'bg-yellow-500' :
                                'bg-gray-400'
                              )} />
                              {agent.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-lg font-semibold text-[#393d8a]">{agent.total_messages}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-gray-900">{agent.assigned_tickets}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="text-[#30a34b] font-medium">{agent.resolved_tickets}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={cn(
                              "font-medium",
                              agent.open_tickets > 5 ? "text-red-500" : "text-gray-600"
                            )}>
                              {agent.open_tickets}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {(!agentActivity.agent_stats || agentActivity.agent_stats.length === 0) && (
                    <div className="p-8 text-center text-gray-500">
                      No agent activity data for the selected period
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center text-gray-500">
                Select a date range and click Apply to view agent activity
              </div>
            )
            )}
          </div>
        ) : activeTab === 'satisfaction' ? (
          /* Satisfaction Trends Tab */
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Customer Satisfaction Trends</h3>
                  <p className="text-sm text-gray-500 mt-1">Track CSAT scores and feedback over time</p>
                </div>
                <div className="flex gap-2">
                  {['7d', '30d', '90d'].map((period) => (
                    <Button
                      key={period}
                      variant={satisfactionPeriod === period ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSatisfactionPeriod(period)}
                      className={satisfactionPeriod === period ? 'bg-[#393d8a]' : ''}
                    >
                      {period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {satisfactionLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#393d8a]" />
              </div>
            ) : satisfactionData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#393d8a] rounded-lg flex items-center justify-center">
                        <ThumbsUp className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">CSAT Score</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{satisfactionData.csat_score}%</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#eeb224] rounded-lg flex items-center justify-center">
                        <Star className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">Avg Rating</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{satisfactionData.average_rating}/5</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-[#30a34b] rounded-lg flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">Responses</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{satisfactionData.total_responses}</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-600 font-medium">Satisfied (4-5)</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">
                      {(satisfactionData.ratings_distribution?.[4] || 0) + (satisfactionData.ratings_distribution?.[5] || 0)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  {/* Rating Distribution */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-900 mb-4">Rating Distribution</h3>
                    <div className="space-y-3">
                      {[5, 4, 3, 2, 1].map((rating) => {
                        const count = satisfactionData.ratings_distribution?.[rating] || 0;
                        const total = satisfactionData.total_responses || 1;
                        const percentage = Math.round((count / total) * 100);
                        return (
                          <div key={rating} className="flex items-center gap-3">
                            <div className="flex items-center gap-1 w-12">
                              <Star className={cn("w-4 h-4", rating >= 4 ? "text-[#eeb224] fill-[#eeb224]" : "text-gray-300")} />
                              <span className="text-sm font-medium">{rating}</span>
                            </div>
                            <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className={cn(
                                  "h-full rounded-full transition-all",
                                  rating >= 4 ? "bg-green-500" : rating === 3 ? "bg-yellow-500" : "bg-red-500"
                                )}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trend Chart */}
                  <div className="col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="font-semibold text-gray-900 mb-4">Satisfaction Trend</h3>
                    {satisfactionData.trend_data && satisfactionData.trend_data.length > 0 ? (
                      <div className="h-48 flex items-end gap-1">
                        {satisfactionData.trend_data.slice(-14).map((day, index) => {
                          const height = day.avg > 0 ? (day.avg / 5) * 100 : 0;
                          return (
                            <div key={index} className="flex-1 flex flex-col items-center gap-1">
                              <div className="w-full flex flex-col items-center">
                                {day.count > 0 && (
                                  <span className="text-xs text-gray-500 mb-1">{day.avg.toFixed(1)}</span>
                                )}
                                <div 
                                  className={cn(
                                    "w-full rounded-t transition-all",
                                    day.avg >= 4 ? "bg-green-500" : day.avg >= 3 ? "bg-yellow-500" : day.avg > 0 ? "bg-red-500" : "bg-gray-200"
                                  )}
                                  style={{ height: `${Math.max(height, 4)}%` }}
                                  title={`${day.date}: ${day.avg.toFixed(1)} avg (${day.count} responses)`}
                                />
                              </div>
                              <span className="text-xs text-gray-400 transform -rotate-45 origin-top-left mt-2">
                                {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-48 flex items-center justify-center text-gray-500">
                        No trend data available yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Agent Performance */}
                {satisfactionData.agent_performance && satisfactionData.agent_performance.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4 border-b border-gray-100">
                      <h3 className="font-semibold text-gray-900">Agent Satisfaction Rankings</h3>
                    </div>
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Rank</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Agent</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Reviews</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Avg Rating</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {satisfactionData.agent_performance.map((agent, index) => (
                          <tr key={agent.agent_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <span className={cn(
                                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                index === 0 ? "bg-[#eeb224] text-white" : 
                                index === 1 ? "bg-gray-300 text-gray-700" :
                                index === 2 ? "bg-orange-300 text-orange-800" :
                                "bg-gray-100 text-gray-600"
                              )}>
                                {index + 1}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-medium text-gray-900">{agent.agent_name}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-gray-600">{agent.reviews}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Star className="w-4 h-4 text-[#eeb224] fill-[#eeb224]" />
                                <span className="font-semibold text-gray-900">{agent.avg_rating}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                <ThumbsUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No satisfaction data available yet</p>
                <p className="text-sm text-gray-400 mt-1">Satisfaction responses will appear here once customers submit feedback</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AnalyticsView;
